import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  Radio,
  Server,
} from "lucide-react";
import { API_BASE_URL } from "../../config";
import {
  establishBullBoardSession,
  fetchJobOverview,
  triggerJob,
  selectJobsError,
  selectBullBoardOpening,
  selectJobOverview,
  selectJobOverviewLoading,
  selectTriggerLoading,
  clearJobsError,
} from "../../redux/slices/jobsSlice";
import AdminSystemChrome from "../system/AdminSystemChrome";
import SystemJobCard from "../system/SystemJobCard";
import {
  countHealthySchedulers,
  filterJobs,
  formatJobTimeLeft,
  formatLocalStamp,
  getUpcomingJobs,
  groupJobsByQueue,
  jobMsUntilNext,
  listQueues,
} from "../system/systemHelpers";
import "../styles/admin-markets.css";
import "../system/admin-system.css";

export default function SystemPage() {
  const dispatch = useDispatch();
  const error = useSelector(selectJobsError);
  const bullBoardOpening = useSelector(selectBullBoardOpening);
  const overview = useSelector(selectJobOverview);
  const overviewLoading = useSelector(selectJobOverviewLoading);
  const triggerLoading = useSelector(selectTriggerLoading);
  const [now, setNow] = useState(() => Date.now());
  const [queueFilter, setQueueFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchJobOverview());
  }, [dispatch]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => dispatch(fetchJobOverview()), 20000);
    return () => window.clearInterval(id);
  }, [dispatch]);

  const jobs = overview?.jobs || [];
  const queues = useMemo(() => listQueues(jobs), [jobs]);
  const visibleJobs = useMemo(
    () => filterJobs(jobs, { queue: queueFilter, search }),
    [jobs, queueFilter, search]
  );
  const grouped = useMemo(() => groupJobsByQueue(visibleJobs), [visibleJobs]);
  const upcoming = useMemo(() => getUpcomingJobs(jobs, 6, now), [jobs, now]);
  const healthyCount = useMemo(() => countHealthySchedulers(jobs), [jobs]);

  const handleRefresh = () => dispatch(fetchJobOverview());

  const handleOpenQueueMonitor = async () => {
    dispatch(clearJobsError());
    if (process.env.NODE_ENV === "production") {
      try {
        await dispatch(establishBullBoardSession()).unwrap();
      } catch {
        return;
      }
    }
    window.open(`${API_BASE_URL}/admin/jobs`, "_blank", "noopener,noreferrer");
  };

  const handleTrigger = async (jobName) => {
    if (!window.confirm(`Run job "${jobName}" now? This enqueues one run on Redis.`)) return;
    dispatch(clearJobsError());
    try {
      await dispatch(triggerJob({ jobName })).unwrap();
      toast.success(`Queued: ${jobName}`);
      dispatch(fetchJobOverview());
    } catch (e) {
      toast.error(e?.message || "Trigger failed");
    }
  };

  const redisOk = Boolean(overview?.redis);
  const isLoading = overviewLoading && !jobs.length;

  return (
    <div className="am-page asys-page">
      <div className="am-aurora" aria-hidden>
        <span className="am-aurora-a asys-aurora" />
      </div>

      <AdminSystemChrome
        loading={overviewLoading}
        onRefresh={handleRefresh}
        overview={overview}
        jobCount={jobs.length}
        healthyCount={healthyCount}
      />

      <div className="asys-desk">
        <div className="asys-detail-timing" aria-label="Infrastructure status">
          <div className="asys-detail-time">
            <span className="asys-detail-time-key">Redis</span>
            <span className={`asys-detail-time-val${redisOk ? " asys-val-ok" : " asys-val-warn"}`}>
              {redisOk ? "Connected" : "Offline"}
            </span>
          </div>
          <div className="asys-detail-time">
            <span className="asys-detail-time-key">Schedulers</span>
            <span className="asys-detail-time-val">{healthyCount}</span>
          </div>
          <div className="asys-detail-time">
            <span className="asys-detail-time-key">Jobs</span>
            <span className="asys-detail-time-val">{jobs.length}</span>
          </div>
          <div className="asys-detail-time">
            <span className="asys-detail-time-key">Server clock</span>
            <span className="asys-detail-time-val">
              {overview?.now ? formatLocalStamp(overview.now) : "—"}
            </span>
          </div>
        </div>

        <section className="asys-launch">
          <div className="asys-launch-copy">
            <p className="asys-launch-title">Queue monitor</p>
            <p className="asys-launch-sub">
              BullMQ board on the API origin. Production opens a short-lived session first.
            </p>
          </div>
          <button
            type="button"
            className="asys-launch-btn"
            onClick={handleOpenQueueMonitor}
            disabled={bullBoardOpening}
          >
            {bullBoardOpening ? (
              <>
                <Loader2 size={14} className="am-spin" aria-hidden />
                Opening…
              </>
            ) : (
              <>
                <ExternalLink size={14} aria-hidden />
                Open monitor
              </>
            )}
          </button>
        </section>

        {overview && !overview.redis ? (
          <div className="asys-alert" role="alert">
            <AlertTriangle size={16} aria-hidden />
            <span>
              Redis is not connected — set <code>REDIS_ENABLED=true</code> and a reachable{" "}
              <code>REDIS_URL</code>.
            </span>
          </div>
        ) : null}

        {error ? (
          <div className="asys-alert asys-error" role="alert">
            <AlertTriangle size={16} aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}

        {upcoming.length ? (
          <div className="asys-detail-timing asys-upcoming" aria-label="Upcoming runs">
            {upcoming.map((job) => (
              <div key={job.name} className="asys-detail-time">
                <span className="asys-detail-time-key" title={job.name}>
                  {job.name}
                </span>
                <span className="asys-detail-time-val">
                  {formatJobTimeLeft(jobMsUntilNext(job, now))}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="asys-toolbar">
          <label className="asys-search">
            <Radio size={13} aria-hidden />
            <input
              type="search"
              placeholder="Filter jobs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter jobs"
            />
          </label>
          <button
            type="button"
            className={`asys-chip${queueFilter === "all" ? " asys-chip-on" : ""}`}
            onClick={() => setQueueFilter("all")}
          >
            All queues
          </button>
          {queues.map((queue) => (
            <button
              key={queue}
              type="button"
              className={`asys-chip${queueFilter === queue ? " asys-chip-on" : ""}`}
              onClick={() => setQueueFilter(queue)}
            >
              {queue}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="asys-empty">
            <Loader2 size={28} className="am-spin" aria-hidden />
            <p>Loading job manifests…</p>
          </div>
        ) : !visibleJobs.length ? (
          <div className="asys-empty">
            <Server size={28} aria-hidden />
            <h2>No jobs match</h2>
            <p>Try clearing your queue filter or search query.</p>
          </div>
        ) : (
          <div className="asys-board">
            {grouped.map(({ queue, accent, jobs: queueJobs }) => (
              <section key={queue} className="asys-queue-section">
                <div className="asys-queue-head">
                  <span className="asys-queue-dot" style={{ background: accent, color: accent }} />
                  <h2 className="asys-queue-title">{queue}</h2>
                  <span className="asys-queue-count">{queueJobs.length}</span>
                </div>
                <div className="asys-job-list">
                  {queueJobs.map((job) => (
                    <SystemJobCard
                      key={job.name}
                      job={job}
                      now={now}
                      redisOk={redisOk}
                      triggering={Boolean(triggerLoading[job.name])}
                      onRun={handleTrigger}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
